import { useCallback, useEffect, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import { parseDate } from "shared/utils/dateUtils";

export interface LessonStat {
    id: string;
    name: string;
    teacherName: string;
    days: string;
    studentCount: number;
}

export interface NamedCount {
    id: string;
    name: string;
    value: number;
}

export interface TeacherStats {
    branchName: string;
    teacherCount: number;
    studentCount: number;
    activeStudentCount: number;
    suspendedStudentCount: number;
    graduatedStudentCount: number;
    unpaidCount: number;
    unpaidAmount: number;
    lessonCount: number;
    assignedStudentCount: number;
    activeReleaseCount: number;
    monthScore: number;
    scoreStudentCount: number;
    lessons: LessonStat[];
    topScores: NamedCount[];
    unpaidStudents: NamedCount[];
}

const emptyStats: TeacherStats = {
    branchName: "",
    teacherCount: 0,
    studentCount: 0,
    activeStudentCount: 0,
    suspendedStudentCount: 0,
    graduatedStudentCount: 0,
    unpaidCount: 0,
    unpaidAmount: 0,
    lessonCount: 0,
    assignedStudentCount: 0,
    activeReleaseCount: 0,
    monthScore: 0,
    scoreStudentCount: 0,
    lessons: [],
    topScores: [],
    unpaidStudents: [],
};

interface StudentRow {
    id: string;
    name: string;
    status: string;
    unpaied: number;
}

const docsOf = (res: any) => {
    if (!res || res.empty || !Array.isArray(res.docs)) return [];
    return res.docs;
};

const studentStatus = (status?: string) => status || "active";

const useTeacherStats = () => {
    const { readFirestore, readFirestoreOneDoc, readFirestoreWithQuery } = useFirestore();
    const { getBranchId } = useGetBranchId();
    const [stats, setStats] = useState<TeacherStats>(emptyStats);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        const today = parseDate(new Date());
        const year = new Date().getFullYear();
        const month = new Date().getMonth() + 1;

        try {
            const branchId = await getBranchId();
            const [
                branchDoc,
                teacherSnap,
                studentSnap,
                lessonSnap,
                assignSnap,
                releaseSnap,
                scoreSnap,
            ] = await Promise.all([
                readFirestoreOneDoc("User/Auth/Branch", branchId),
                readFirestoreWithQuery("User/Auth/Teacher", { field: "branch_id", operator: "==", data: branchId }),
                readFirestoreWithQuery("User/Auth/Student", { field: "branch_id", operator: "==", data: branchId }),
                readFirestore(`Lesson/Branch/${branchId}`),
                readFirestore(`Assign/${branchId}/Lesson`),
                readFirestore(`ReportAssignBranch/${branchId}/Report`),
                readFirestore(`TossScore/${branchId}/${year}/${month}/Score`),
            ]);

            const teacherNames: Record<string, string> = {};
            docsOf(teacherSnap).forEach((doc: any) => {
                teacherNames[doc.id] = doc.data().name || "이름 없음";
            });

            const students: StudentRow[] = docsOf(studentSnap).map((doc: any) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    name: data.name || "이름 없음",
                    status: studentStatus(data.status),
                    unpaied: Number(data.unpaied) || 0,
                };
            });
            const studentNames: Record<string, string> = {};
            students.forEach(item => {
                studentNames[item.id] = item.name;
            });

            const assignCount: Record<string, number> = {};
            const assignedIds = new Set<string>();
            docsOf(assignSnap).forEach((doc: any) => {
                const ids = Array.isArray(doc.data().students) ? doc.data().students : [];
                assignCount[doc.id] = ids.length;
                ids.forEach((id: string) => assignedIds.add(id));
            });

            const lessons: LessonStat[] = docsOf(lessonSnap).map((doc: any) => {
                const data = doc.data();
                const days = Array.isArray(data.days) ? data.days.join(" ") : "";
                return {
                    id: doc.id,
                    name: data.name || "이름 없음",
                    teacherName: teacherNames[data.teacher] || "미지정",
                    days,
                    studentCount: assignCount[doc.id] || 0,
                };
            });
            lessons.sort((a, b) => b.studentCount - a.studentCount || a.name.localeCompare(b.name, "ko"));

            let activeReleaseCount = 0;
            docsOf(releaseSnap).forEach((doc: any) => {
                const range = doc.data().effective_date;
                if (range?.start && range?.end && range.start <= today && today <= range.end) {
                    activeReleaseCount += 1;
                }
            });

            const scores: NamedCount[] = docsOf(scoreSnap).map((doc: any) => ({
                id: doc.id,
                name: studentNames[doc.id] || "이름 없음",
                value: Number(doc.data().score) || 0,
            }));
            scores.sort((a, b) => b.value - a.value || a.name.localeCompare(b.name, "ko"));

            const activeStudents = students.filter(item => item.status === "active");
            const unpaidStudents = students
                .filter(item => item.unpaied > 0)
                .sort((a, b) => b.unpaied - a.unpaied || a.name.localeCompare(b.name, "ko"));

            setStats({
                branchName: branchDoc.exists() ? (branchDoc.data()?.name || "우리 지점") : "우리 지점",
                teacherCount: docsOf(teacherSnap).length,
                studentCount: students.length,
                activeStudentCount: activeStudents.length,
                suspendedStudentCount: students.filter(item => item.status === "suspended").length,
                graduatedStudentCount: students.filter(item => item.status === "graduated").length,
                unpaidCount: unpaidStudents.length,
                unpaidAmount: unpaidStudents.reduce((sum: number, item: StudentRow) => sum + item.unpaied, 0),
                lessonCount: lessons.length,
                assignedStudentCount: assignedIds.size,
                activeReleaseCount,
                monthScore: scores.reduce((sum: number, item: NamedCount) => sum + item.value, 0),
                scoreStudentCount: scores.length,
                lessons,
                topScores: scores.slice(0, 10),
                unpaidStudents: unpaidStudents.map(item => ({
                    id: item.id,
                    name: item.name,
                    value: item.unpaied,
                })),
            });
        } catch (error) {
            console.error(error);
            setIsError(true);
            setStats(emptyStats);
        } finally {
            setIsLoading(false);
        }
    }, [getBranchId, readFirestore, readFirestoreOneDoc, readFirestoreWithQuery]);

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    return { stats, isLoading, isError, onRefresh: load };
};

export default useTeacherStats;
